import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { useAuth } from "../contexts/AuthContext";
import Hero from "./Hero";
import "./Home.css";

const LightningIcon = () => (
  <svg width="28" height="28" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
    <path d="M13 2L3 14H12L11 22L21 10H12L13 2Z" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" fill="currentColor" fillOpacity="0.15" />
  </svg>
);

const ScaleIcon = () => (
  <svg width="28" height="28" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
    <path d="M12 3v18M5 12H2l3-6 3 6H5zm14 0h-3l3-6 3 6h-3z" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
  </svg>
);

const BriefcaseIcon = () => (
  <svg width="28" height="28" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
    <rect x="2" y="7" width="20" height="14" rx="2" stroke="currentColor" strokeWidth="2" fill="currentColor" fillOpacity="0.08" />
    <path d="M16 21V5a2 2 0 0 0-2-2H10a2 2 0 0 0-2 2V21" stroke="currentColor" strokeWidth="2" />
  </svg>
);

const ShieldIcon = () => (
  <svg width="28" height="28" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
    <path d="M12 22S8 18 8 12V7l4-2 4 2V12C16 18 12 22 12 22Z" stroke="currentColor" strokeWidth="2" fill="currentColor" fillOpacity="0.08" />
    <path d="M9 12L11 14L15 10" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
  </svg>
);

const ChartIcon = () => (
  <svg width="28" height="28" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
    <path d="M3 3V21H21" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
    <path d="M9 9L12 6L16 10L22 4" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
  </svg>
);

const GlobeIcon = () => (
  <svg width="28" height="28" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
    <circle cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="2" />
    <path d="M2 12H22" stroke="currentColor" strokeWidth="2" />
    <path d="M12 2A15.3 15.3 0 0 1 16 12A15.3 15.3 0 0 1 12 22A15.3 15.3 0 0 1 8 12A15.3 15.3 0 0 1 12 2Z" stroke="currentColor" strokeWidth="2" />
  </svg>
);

const features = [
  {
    icon: <LightningIcon />,
    title: "Instant Resolution",
    desc: "AI-generated decisions with confidence scoring delivered in real-time — not weeks.",
  },
  {
    icon: <ScaleIcon />,
    title: "Impartial Analysis",
    desc: "Decisions grounded in established legal rules and procedures, free of human bias.",
  },
  {
    icon: <BriefcaseIcon />,
    title: "eCommerce Focused",
    desc: "Built for contract, service, product, and delivery disputes under $1,000 USD.",
  },
  {
    icon: <ShieldIcon />,
    title: "Secure & Private",
    desc: "Enterprise-grade security protects every submission and decision record.",
  },
  {
    icon: <ChartIcon />,
    title: "Confidence Scoring",
    desc: "Every verdict includes a confidence score so you understand the certainty behind it.",
  },
  {
    icon: <GlobeIcon />,
    title: "24/7 Availability",
    desc: "Submit and resolve disputes any time — our AI never sleeps.",
  },
];

const Home: React.FC = () => {
  const [isVisible, setIsVisible] = useState(false);
  const navigate = useNavigate();
  const { isAuthenticated } = useAuth();

  useEffect(() => {
    const timer = setTimeout(() => setIsVisible(true), 80);
    return () => clearTimeout(timer);
  }, []);

  const scrollToContent = () => {
    const el = document.querySelector(".features-section");
    if (el) el.scrollIntoView({ behavior: "smooth" });
  };

  return (
    <div className="home-container">
      <div className="home-content">
        {/* Hero */}
        <div className="hero-section-wrapper">
          <Hero />
          <button className="scroll-indicator" onClick={scrollToContent} aria-label="Scroll to features">
            <span>Explore</span>
            <svg className="scroll-arrow" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
              <path d="M7 10l5 5 5-5" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
            </svg>
          </button>
        </div>

        {/* Features */}
        <section className="features-section">
          <div className={`section-header ${isVisible ? "header-visible" : ""}`}>
            <p className="section-eyebrow">Why LEX</p>
            <h2 className="section-title">Dispute resolution,<br />redesigned.</h2>
            <p className="section-desc">
              Traditional arbitration takes months and costs thousands. LEX cuts that to minutes.
            </p>
          </div>

          <div className="features-grid">
            {features.map((f, i) => (
              <div
                key={f.title}
                className={`feature-item ${isVisible ? "item-visible" : ""}`}
                style={{ transitionDelay: `${0.5 + i * 0.1}s` }}
              >
                <div className="feature-icon">{f.icon}</div>
                <div className="feature-body">
                  <h3 className="feature-title">{f.title}</h3>
                  <p className="feature-desc">{f.desc}</p>
                </div>
              </div>
            ))}
          </div>

          <div className={`cta-row ${isVisible ? "cta-visible" : ""}`}>
            <button className="cta-btn" onClick={() => navigate("/rain")}>
              Request a Decision
            </button>
            {!isAuthenticated && (
              <p className="cta-note">No account required to explore.</p>
            )}
          </div>
        </section>
      </div>
    </div>
  );
};

export default Home;
